import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { APPOINTMENT_ID_LENGTH } from "@/constants/appointment";
import { api } from "@/services/api";
import { tokenStorage } from "@/utils/storage";
import { checkIsAdmin } from "@/utils/users";
import { fetchAppointmentData } from "../appointment/slice";
import type { IUserState } from "./types";

export const fetchUserData = createAsyncThunk(
  'user/fetchUserData',
  async (roomId: string, { dispatch }) => {
    if (!tokenStorage.getItem()) {
      return null;
    }

    const response = await api.get('/auth/me');

    if (roomId.length === APPOINTMENT_ID_LENGTH) {
      dispatch(fetchAppointmentData(roomId));
    }

    const isAdmin = checkIsAdmin(response.data.data.id);

    return isAdmin ? {
      ...response.data.data,
      avatar: 'medicusunion-app-icon.png',
      first_name: 'MedicusUnion',
      last_name: 'Support',
    } : response.data.data;
  }
);

const initialState: IUserState = {
  user: null,
  isVideoEnabled: true,
  isAudioEnabled: true,
  loading: true,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    toggleUserLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    toggleLocalAudioEnabled(state, action: PayloadAction<boolean>) {
      state.isAudioEnabled = action.payload;
    },
    toggleLocalVideoEnabled(state, action: PayloadAction<boolean>) {
      state.isVideoEnabled = action.payload;
    },
    setUserName(state, action: PayloadAction<string>) {
      if (action.payload) {
        state.user = {
          ...(state.user || {}),
          name: action.payload
        };
      }
    },
    resetUser(state) {
      state.isAudioEnabled = initialState.isAudioEnabled;
      state.isVideoEnabled = initialState.isVideoEnabled;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserData.fulfilled, (state, action) => {
        state.user = action.payload ? {
          id: action.payload.id,
          name: `${action.payload.first_name} ${action.payload.last_name}`,
          avatar: action.payload.avatar || null,
        } : null;
        state.loading = false;
      })
      .addCase(fetchUserData.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  resetUser,
  toggleUserLoading,
  toggleLocalAudioEnabled,
  toggleLocalVideoEnabled,
  setUserName,
} = userSlice.actions;

export default userSlice.reducer;
